import React, { Fragment, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Layout, Menu } from 'antd';
import { CaretDownOutlined, CaretUpOutlined, MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';

import './styles.scss';
import { useBranding } from 'hooks/useBranding';

import * as routePath from 'routers/route-path';
import { USER_LEVEL_TYPE, USER_ROLE_TYPES } from 'constants/common';
import menu from './menu';
import useProfile from 'hooks/useProfile';
import { checkValidRole, getLogoComponent } from 'utilities/common';
import { removeDuplicatesArray } from 'utilities/common';
import Icon, { RightIcon } from 'components/Atoms/Icon';
import { ACCOUNT_ROLE_ON_URL } from 'constants/url';

const { Sider } = Layout;
const { SubMenu } = Menu;

const SideBar = ({ collapsed: propCollapsed = false, onCollapseChange }) => {
  const location = useLocation();

  const [menuSelected, setMenuSelected] = useState({
    selectKey: [],
    openKey: [],
  });

  const [isCollapsed, setIsCollapsed] = useState(propCollapsed);

  const { data: userInfo } = useProfile();
  const { branding } = useBranding();

  const homeLink =
    userInfo?.level !== USER_LEVEL_TYPE.ANALYTIC_MANAGER
      ? '/'
      : routePath.ANALYTICS.replace(
          ':accountRole',
          ACCOUNT_ROLE_ON_URL[userInfo.role]
        );

  // Sync with prop changes
  useEffect(() => {
    setIsCollapsed(propCollapsed);
  }, [propCollapsed]);

  // Notify parent of collapse state changes
  useEffect(() => {
    if (onCollapseChange) {
      onCollapseChange(isCollapsed);
    }
  }, [isCollapsed, onCollapseChange]);

  useEffect(() => {
    let temp = { ...menuSelected, selectKey: [] };
    const { pathname } = location;
    for (const item of menu) {
      if (
        item.path === pathname ||
        pathname.includes(item?.path?.replace(':id', ''))
      ) {
        temp = {
          selectKey: [item.name || item.parentName],
          openKey: [...menuSelected.openKey],
        };
        break;
      }
      if (item.children?.length) {
        for (const itemSub of item.children) {
          if (
            itemSub.path === pathname ||
            pathname.includes(itemSub?.path.replace(':id', ''))
          ) {
            temp = {
              selectKey: [itemSub.parentName || itemSub.name],
              openKey: [...menuSelected.openKey, item.name],
            };
            break;
          }
        }
      }
    }
    setMenuSelected({
      ...temp,
      openKey: removeDuplicatesArray(temp.openKey),
    });
  }, [location]);

  const renderMenu = () => {
    return (
      <Menu
        selectedKeys={menuSelected.selectKey}
        openKeys={menuSelected.openKey}
        mode="inline"
        id="sidebar-menu-custom"
        onOpenChange={(openKeys) =>
          setMenuSelected({ ...menuSelected, openKey: openKeys })
        }
        expandIcon={({ isOpen }) => {
          return (
            <div className="d-inline-block f-right">
              {isOpen ? (
                <CaretUpOutlined className="icon-expand" />
              ) : (
                <CaretDownOutlined className="icon-expand" />
              )}
            </div>
          );
        }}
      >
        {menu.map((m) => {
          if (m.children) {
            return renderSubMenu(m);
          }
          return renderMenuItem(m);
        })}
      </Menu>
    );
  };

  const renderSubMenu = (data) => {
    return (
      <SubMenu
        key={data.name}
        icon={<Icon component={data.icon} className="my-menu-icon" />}
        title={data?.name}
      >
        {data.children.map((d) => renderMenuItem(d, true))}
      </SubMenu>
    );
  };

  const renderMenuItem = (data, isSub) => {
    if (
      data.path === routePath.ADVERTISERS &&
      userInfo?.role === USER_ROLE_TYPES.CAMPAIGN
    ) {
      return null;
    }

    if (
      !data.showMenu ||
      !checkValidRole({
        userInfo,
        roles: data.roles,
        condition: data.condition,
        isPrivate: true,
        defaultShow: data.defaultShow || false,
      })
    ) {
      return null;
    }

    const path =
      userInfo?.role && data.path.includes(':accountRole')
        ? data.path.replace(':accountRole', ACCOUNT_ROLE_ON_URL[userInfo.role])
        : data.path;

    return (
      <Fragment key={path}>
        <Menu.Item
          key={data.name}
          icon={
            <Icon
              component={isSub ? RightIcon : data.icon}
              className="my-menu-icon"
            />
          }
        >
          <Link to={path}>{data.name}</Link>
        </Menu.Item>

        {data.hasBorderBt && <div className="border-bt" />}
      </Fragment>
    );
  };

  const LogoComponent = getLogoComponent(branding?.logo);
  const LogoSmallComponent = getLogoComponent(branding?.logoSmall);

  const handleToggle = () => {
    setIsCollapsed((prev) => !prev);
  };

  return (
    <Sider
      collapsed={isCollapsed}
      collapsedWidth={100}
      id="site-sidebar"
      width={260}
    >
      <Link to={homeLink}>
        <div className="logo-wrapper">
          {isCollapsed ? <LogoSmallComponent /> : <LogoComponent />}
        </div>
      </Link>
      {renderMenu()}
      <div className="sidebar-toggle" onClick={handleToggle}>
        {isCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
      </div>
    </Sider>
  );
};

export default SideBar;
